import { Box, Flex, Heading, Input } from "@chakra-ui/react";
import { useController, UseFormReturn } from "react-hook-form";
import { getFragmentData, gql } from "__generated__/gql";
import {
  ControlledFormField,
  FormField,
  SkuInputs,
} from "components/Products/SkuPanel/utils";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import DeleteConfirmModalButton from "components/Shared/ConfirmModal/DeleteConfirmModalButton";
import useLocalMutation from "utils/useLocalMutation";
import NumberInput from "components/Shared/Forms/NumberInput";
import DistributionCenterSelect from "components/Shared/Select/DistributionCenterSelect";

export const skuOrderingPlatformFragment = gql(/* GraphQL */ `
  fragment SkuOrderingPlatformsOrderingPlatformDetails on OrderingPlatform {
    id
    name
    distributionCenters(filter: { name: { isNull: false } }) {
      totalCount
    }
  }
`);

const skuOrderingPlatformQuery = gql(/* GraphQL */ `
  query SkuOrderingPlatformFieldElementDetails($orderingPlatformId: UUID!) {
    orderingPlatform(id: $orderingPlatformId) {
      id
      ...SkuOrderingPlatformsOrderingPlatformDetails
    }
  }
`);

type Props = {
  formContext: UseFormReturn<SkuInputs>;
  index: number;
  onDelete: (orderingPlatformId: string) => void;
};

export default function SkuOrderingPlatform({
  formContext,
  index,
  onDelete,
}: Props) {
  const { control } = formContext;
  const {
    field: { value },
  } = useController({ control, name: `orderingPlatformData.${index}` });

  const { orderingPlatformId } = value || {};

  const { data } = useBrandQuery(skuOrderingPlatformQuery, {
    skip: !orderingPlatformId,
    variables: { orderingPlatformId },
  });

  const deleteSkuOrderingPlatform = useLocalMutation(() =>
    onDelete(orderingPlatformId),
  );

  const op = getFragmentData(
    skuOrderingPlatformFragment,
    data?.orderingPlatform,
  );

  return (
    <Box
      as="li"
      listStyleType="none"
      borderColor="gray.300"
      borderWidth={1}
      p={2}
      my={4}
      borderRadius="md"
    >
      <Flex>
        <Heading as="h3" size="xs" color="magenta.dark">
          {op?.name}
        </Heading>
        <DeleteConfirmModalButton
          mutation={deleteSkuOrderingPlatform}
          label={`Remove ${op?.name || "wholesaler"}`}
          confirmButtonLabel="Remove"
          buttonProps={{ ml: "auto", size: "xs" }}
          confirmText={
            <>
              Are you sure you want to remove <b>{op?.name}</b> and its
              distribution centers from this SKU?
            </>
          }
        />
      </Flex>

      <FormField
        name={`orderingPlatformData.${index}.itemCode`}
        renderInput={(props) => (
          <Input
            {...props}
            fontFamily="mono"
            placeholder={`${op?.name || "this wholesaler"} item code`}
          />
        )}
      />

      <ControlledFormField
        name={`orderingPlatformData.${index}.wholesaleUnitPrice`}
        renderInput={({ field }) => (
          <NumberInput
            {...field}
            max={100000}
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            allowNegative={false}
            placeholder={`${
              op?.name || "this wholesaler"
            } wholesale unit price`}
          />
        )}
        mb={0}
      />

      {!!op?.distributionCenters?.totalCount && (
        <ControlledFormField
          name={`orderingPlatformData.${index}.distributionCenterIds`}
          renderInput={({ field }) => (
            <DistributionCenterSelect
              isMulti
              {...field}
              {...{ orderingPlatformId }}
              filter={{ name: { isNull: false } }}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          )}
          mb={0}
          labelProps={{ mb: 1 }}
        />
      )}
    </Box>
  );
}
